import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetReportQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../../shared/AuthRoles';
import { MailingListType, MailingListUser } from '../../../../types/user';
import { openDashboardReportExportMenu } from '../../../Dashboard/dashboardReportTableConfig';
import MailingListReportList from './MailingListReportList';

const userTypeOptions: { value: MailingListType; label: string }[] = [
    { value: 'guest', label: 'Guest' },
    { value: 'owner', label: 'Host' },
    { value: 'contractor', label: 'Contractor' },
];

export default function MailingListsReportPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const [userType, setUserType] = useState<MailingListType>('guest');

    useEffect(() => {
        if (loggedInUser.role !== 'admin') {
            navigate('/permissionDenied');
        }
    }, [loggedInUser.role, navigate]);

    const { data, isLoading } = useGetReportQuery(
        `/reports/mailing-list?type=${userType}`,
        {
            skip: loggedInUser.role !== 'admin',
        },
    );

    const userTypeLabel = useMemo(
        () =>
            userTypeOptions.find((option) => option.value === userType)
                ?.label ?? 'Guest',
        [userType],
    );

    const users = (data ?? []) as MailingListUser[];

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <Box sx={{ top: isMobile ? '30px' : '0px' }}>
            <Grid
                container
                justifyContent='space-between'
                spacing={1}
                alignItems='center'
                sx={{ paddingBottom: 2 }}
            >
                <Grid item xs={12} md={4}>
                    <FormControl size='small' fullWidth>
                        <InputLabel id='mailing-list-type-label'>
                            User type
                        </InputLabel>
                        <Select
                            labelId='mailing-list-type-label'
                            value={userType}
                            label='User type'
                            onChange={(event) => {
                                setUserType(
                                    event.target.value as MailingListType,
                                );
                            }}
                        >
                            {userTypeOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isMobile
                                ? 'flex-start'
                                : 'flex-end',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        <CustomButton
                            disableRipple
                            variant='contained'
                            color='neutral'
                            onClick={openDashboardReportExportMenu}
                        >
                            <FileDownloadIcon fontSize='small' />
                        </CustomButton>
                    </Box>
                </Grid>
            </Grid>

            <Typography variant='h5' sx={{ mb: 2 }}>
                {users.length.toLocaleString()} active{' '}
                {userTypeLabel.toLowerCase()} users
            </Typography>

            <MailingListReportList data={users} userTypeLabel={userTypeLabel} />
        </Box>
    );
}
